import { observer }            from 'mobx-react-lite';
import BadgeSettings           from '@/TokamapEditor/ux/AreaSidebar/BadgeSettings';
import currentEditorStore      from '@/TokamapEditor/stores/currentEditorStore';
import { commonValue }         from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import { badgeEligibleShapes } from '@/TokamapEditor/helpers/shapeBadge';
import { effectiveBadge }      from '@/TokamapEditor/helpers/shapeBadge';
import { badgeFieldLock }      from '@/TokamapEditor/helpers/shapeBadge';
import { textPositionOf }      from '@/TokamapEditor/helpers/shapeBadge';
import HelpTip                 from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import type { ShapeBadge }     from '@tokamap/core';

export const BADGE_MODES_HELP = `Dans la forme : à son échelle, ils tournent avec la carte. En marqueur : au-dessus de la forme, à l'endroit et de la même taille à l'écran à tous les zooms.`;

const BadgeDisplayField = observer(() => {
	const editor = currentEditorStore.get;

	const shapes = badgeEligibleShapes(currentEditorStore.sidebarShapes);

	if (!shapes.length) return null;

	const ids = shapes.map(shape => shape.id);
	const standalone = shapes.every(shape => shape.shapeKind === 'marker');
	const badge = shapes[0]!.badge ?? {};
	const update = (patch: Partial<ShapeBadge>) => editor.setShapeBadge(ids, { ...badge, ...withoutDefaults(patch) });

	return (
		<BadgeSettings
			label={<>Image et texte<HelpTip>{BADGE_MODES_HELP}</HelpTip></>}
			standalone={standalone}
			modeLockedBy={badgeFieldLock(shapes)}
			locks={{
				style: badgeFieldLock(shapes, 'style'),
				size: badgeFieldLock(shapes, 'size'),
				shape: badgeFieldLock(shapes, 'shape'),
				color: badgeFieldLock(shapes, 'color'),
				anchor: badgeFieldLock(shapes, 'anchor'),
				showText: badgeFieldLock(shapes, 'showText'),
				textPosition: badgeFieldLock(shapes, 'textPosition'),
				minZoomOffset: badgeFieldLock(shapes, 'minZoomOffset'),
			}}
			modes={['Dans la forme', 'En marqueur']}
			mode={commonValue(shapes, shape => (effectiveBadge(shape) ? 'marker' : 'inside'))}
			values={{
				style: commonValue(shapes, shape => effectiveBadge(shape)?.style ?? 'disc'),
				size: commonValue(shapes, shape => effectiveBadge(shape)?.size ?? 'medium'),
				shape: commonValue(shapes, shape => effectiveBadge(shape)?.shape ?? 'round'),
				anchor: commonValue(shapes, shape => effectiveBadge(shape)?.anchor ?? 'center'),
				showText: commonValue(shapes, shape => effectiveBadge(shape)?.showText ?? true),
				textPosition: commonValue(shapes, shape => textPositionOf(effectiveBadge(shape))),
				color: commonValue(shapes, shape => effectiveBadge(shape)?.color),
				colorMixed: new Set(shapes.map(shape => effectiveBadge(shape)?.color)).size > 1,
				minZoomOffset: commonValue(shapes, shape => effectiveBadge(shape)?.minZoomOffset),
				minZoomOffsetMixed: new Set(shapes.map(shape => effectiveBadge(shape)?.minZoomOffset)).size > 1,
			}}
			onMode={mode => editor.setShapeBadge(ids, mode === 'marker' ? {} : undefined)}
			onChange={update}
		/>
	);
});

export default BadgeDisplayField;

/** Une forme n'enregistre que ce qui s'écarte du marqueur par défaut. */
function withoutDefaults(patch: Partial<ShapeBadge>): Partial<ShapeBadge> {
	return {
		...patch,
		...(patch.size === 'medium' ? { size: undefined } : {}),
		...(patch.shape === 'round' ? { shape: undefined } : {}),
		...(patch.showText === true ? { showText: undefined } : {}),
	};
}
